import { formatData } from '@utilities/helpers/data_format';
import {
  COLOR_BANTAMWEIGHT,
  COLOR_SCALE_70,
  COLOR_SCALE_NEGATIVE_50,
} from '@utilities/helpers/colors';

export const DECOMPOSITION_TREE_TYPE = 'sales-change-decomposition-tree';

// cx = card horizontal center as a percentage of the container width.
// row = zero-indexed vertical band, top to bottom.
export const DECOMPOSITION_LAYOUT = {
  node_1: { cx: 66.94, row: 0 },
  node_2: { cx: 50.04, row: 1 },
  node_3: { cx: 83.83, row: 1 },
  node_4: { cx: 27.51, row: 2 },
  node_5: { cx: 72.57, row: 2 },
  node_6: { cx: 16.25, row: 3 },
  node_7: { cx: 38.78, row: 3 },
  node_8: { cx: 61.3, row: 3 },
  node_9: { cx: 83.83, row: 3 },
};

export const DECOMPOSITION_EDGES = [
  ['node_1', ['node_2', 'node_3']],
  ['node_2', ['node_4', 'node_5']],
  ['node_4', ['node_6', 'node_7']],
  ['node_5', ['node_8', 'node_9']],
];

export const ROW_COUNT = Math.max(...Object.values(DECOMPOSITION_LAYOUT).map(({ row }) => row)) + 1;
export const ROW_GAP = 40;

const hasCellValue = (slot) => (slot || slot === 0) && slot !== 'na' && slot !== 'NAN_SENTINEL';

const transformCell = ({ description, format, value }) => {
  if (value === 'NAN_SENTINEL' || description === 'NAN_SENTINEL') return null;
  if (!hasCellValue(value) && !hasCellValue(description)) return null;

  return {
    description: hasCellValue(description) ? formatData({ format, value: description }) : '',
    value: hasCellValue(value) ? formatData({ format, value }) : '',
  };
};

export const transformDecompositionTreeRows = ({ node, rowCount, treeData }) => {
  return Array.from({ length: rowCount }, (_, index) => {
    const rowPrefix = `r${index + 1}`;
    const cells = [
      transformCell({
        description: treeData?.[`${rowPrefix}c1_description`],
        format: node?.[`${rowPrefix}c1_format`],
        value: treeData?.[`${rowPrefix}c1_value`],
      }),
      transformCell({
        description: treeData?.[`${rowPrefix}c2_description`],
        format: node?.[`${rowPrefix}c2_format`],
        value: treeData?.[`${rowPrefix}c2_value`],
      }),
    ].filter(Boolean);

    return { treeCol: cells };
  });
};

export const getDecompositionPctChange = (treeData) => {
  const slots = [treeData?.['r2c2_description'], treeData?.['r2c2_value']];
  if (slots.includes('NAN_SENTINEL')) return undefined;

  return slots.map((candidate) => parseFloat(candidate)).find(Number.isFinite);
};

export const getDecompositionNodeColor = (pctChange) => {
  const pct = parseFloat(pctChange);
  if (pct > 0) return COLOR_SCALE_70;
  if (pct < 0) return COLOR_SCALE_NEGATIVE_50;
  return COLOR_BANTAMWEIGHT;
};

export const buildConnectorPaths = (rowTop, rowMaxHeight, presentIds) => {
  const segments = [];

  DECOMPOSITION_EDGES.forEach(([parent, children]) => {
    if (!presentIds.has(parent)) return;

    const presentChildren = children.filter((child) => presentIds.has(child));
    if (presentChildren.length === 0) return;

    const parentRow = DECOMPOSITION_LAYOUT[parent].row;
    const rowBottom = rowTop[parentRow] + rowMaxHeight[parentRow];
    const childTop = rowTop[parentRow + 1];
    const barY = (rowBottom + childTop) / 2;
    const parentX = DECOMPOSITION_LAYOUT[parent].cx;
    const childXs = presentChildren.map((child) => DECOMPOSITION_LAYOUT[child].cx);
    const barMin = Math.min(parentX, ...childXs);
    const barMax = Math.max(parentX, ...childXs);

    segments.push(`M ${parentX} ${rowBottom} L ${parentX} ${barY}`);
    if (barMax > barMin) segments.push(`M ${barMin} ${barY} L ${barMax} ${barY}`);
    childXs.forEach((x) => segments.push(`M ${x} ${barY} L ${x} ${childTop}`));
  });

  return segments;
};
